import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Divider,
  IconButton,
  Drawer,
  Grid,
  Paper,
  InputBase,
  useTheme,
  Typography
} from "@material-ui/core";
import { 
  Close, 
  SendRounded, 
  AttachFile,
  InsertPhoto,
  PictureAsPdf,
  TableChart,
  VideoLibrary,
  Audiotrack,
  Description,
  InsertDriveFile,
  Cancel
} from "@material-ui/icons";
import LoadChatData from "./LoadChatData";
import { useSelector } from "../../../store";
import * as wss from "../../../utils/wss";
import { getIOSVersion } from "../../../utils/iosVersionGetter";

const ChatSection = (props) => {

  const { roomId, identity, chatHistory } = useSelector((state) => {
    return state.room;
  });

  const currentUser = identity;
  const bottomRef = useRef(null);
  const theme = useTheme();
  const [chatData, setChatData] = useState("");
  const [fileUploaded, setFileUploaded] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileError, setFileError] = useState(null);

  const fileInputRef = useRef(null);
  const attachLastRef = useRef(0);

  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    // console.log("chat-history-change", chatHistory);
    scrollToBottom();
  }, [chatHistory]);

  const canSubmit = Boolean(chatData.trim() || fileUploaded);

  const updateFinalChatData = (event) => {
    if (!canSubmit) return;
    const tempStruct = {
      roomId: roomId,
      identity: currentUser,
      userId: currentUser,
      text: chatData,
      createdAt: new Date(),
      file: fileUploaded
    }
    wss.sendMessage(tempStruct);
    setChatData("");
    setFileUploaded(null);
    setFilePreview(null);
    setFileType(null);
    setFileError(null);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  const updateChatData = (event) => {
    setChatData(event.target.value);
  }

  const keyPressed = (e) => {
    if (e.keyCode == 13) {
      updateFinalChatData();
      e.preventDefault();
    }
  }

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  const getFileIcon = (fileName) => {
    if (!fileName) return InsertDriveFile;
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension)) return InsertPhoto;
    if (extension === 'pdf') return PictureAsPdf;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return TableChart;
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(extension)) return VideoLibrary;
    if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(extension)) return Audiotrack;
    if (['doc', 'docx'].includes(extension)) return Description;
    return InsertDriveFile;
  };

  const getFileIconColor = (fileName) => {
    if (!fileName) return '#757575';
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension)) return '#4caf50';
    if (extension === 'pdf') return '#f44336';
    if (['xls', 'xlsx', 'csv'].includes(extension)) return '#2196f3';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(extension)) return '#9c27b0';
    if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'].includes(extension)) return '#ff9800';
    if (['doc', 'docx'].includes(extension)) return '#2196f3';
    return '#757575';
  };

  const isImageFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'].includes(extension);
  };

  const isVideoFile = (fileName) => {
    if (!fileName) return false;
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'm4v'].includes(extension);
  };

  const uploadFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size >= MAX_FILE_SIZE_BYTES) {
      setFileError("File must be less than 10MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFileError(null);

    setFileUploaded(file);
    setChatData(file.name);
    setFileType(file.type);

    // Create preview for images
    if (isImageFile(file.name)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } 
    // Create preview for videos
    else if (isVideoFile(file.name)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } 
    // For other files, no preview needed
    else {
      setFilePreview(null);
    }
  }

  const removeFile = () => {
    setFileUploaded(null);
    setFilePreview(null);
    setFileType(null);
    setFileError(null);
    setChatData("");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  const handleCloseChatWindowTouchEnd = (e) => {
    e.preventDefault();
    props.closeChatWindow();
  };

  const handleUpdateFinalChatDataTouchEnd = (e) => {
    e.preventDefault();
    updateFinalChatData();
  };

  const openFilePicker = () => {
    const now = Date.now();
    if (now - attachLastRef.current < 800) return; // debounce
    attachLastRef.current = now;
  
    // Must be in the same user gesture handler
    fileInputRef.current?.click();
  };

  const handleAttachTouchEnd = (e) => {
    // IMPORTANT: do NOT preventDefault here, it can break iOS file picker gesture trust
    openFilePicker();
  };

  return (
    <Drawer
      id="chatWindow"
      anchor="right"
      open={props.open}
      onClose={props.closeChatWindow}
    >
      <Box
        sx={{ width: 400, height: '100%' }}
        role="presentation"
      >
        <Grid container spacing={2} sx={{ backgroundColor: "primary.main" }}>
          <Grid item xs={12}>
            <IconButton
              sx={{
                float: "right",
                color: "#ffffff"
              }}
              onClick={props.closeChatWindow}
              onTouchEnd={handleCloseChatWindowTouchEnd}
            >
              <Close />
            </IconButton>
          </Grid>
        </Grid>
        <Divider />
        <Box id="chatHistoryContent" sx={{
          height: fileUploaded ? 'calc(100% - 200px)' : 'calc(100% - 115px)',
          overflow: 'auto',
          p: 1
        }}>
          <LoadChatData currentUser={currentUser} />
          <div ref={bottomRef}></div>
        </Box>
        
        {/* File Preview */}
        {fileUploaded && (
          <Box sx={{
            p: 1,
            ml: 2,
            mr: 2,
            mb: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: '#fafafa',
            position: 'relative'
          }}>
            <IconButton
              size="small"
              onClick={removeFile}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 1
              }}
            >
              <Cancel fontSize="small" />
            </IconButton>

            {isImageFile(fileUploaded.name) && filePreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src={filePreview}
                  alt={fileUploaded.name}
                  sx={{
                    maxWidth: 100,
                    maxHeight: 100,
                    borderRadius: 1,
                    objectFit: 'cover'
                  }}
                />
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                  {fileUploaded.name}
                </Typography>
              </Box>
            ) : isVideoFile(fileUploaded.name) && filePreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <video
                    src={filePreview}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                  {fileUploaded.name}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 60,
                    height: 60,
                    borderRadius: 1,
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  {React.createElement(getFileIcon(fileUploaded.name), {
                    sx: {
                      fontSize: 32,
                      color: getFileIconColor(fileUploaded.name)
                    }
                  })}
                </Box>
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
                  {fileUploaded.name}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {fileError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ ml: 2, mb: 0.5, display: "block" }}
          >
            {fileError}
          </Typography>
        )}

        <Paper component="form" sx={{
          p: '2px 4px',
          ml: 2,
          display: 'flex',
          alignItems: 'center',
          width: 365,
          bottom: (theme) => theme.spacing(2),
          left: (theme) => theme.spacing(2)
        }}>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Type a message"
            inputProps={{ 'aria-label': 'Type a message' }}
            value={chatData}
            onChange={updateChatData}
            onKeyDown={keyPressed}
          />
          {getIOSVersion ? 
          <>
          <input
            ref={fileInputRef}
            type="file"
            onChange={uploadFile}
            style={{
              position: "fixed",
              left: "-10000px",
              top: "0px",
              width: "1px",
              height: "1px",
              opacity: 0,
              pointerEvents: "none",
            }}
          />

          <IconButton
            type="button"
            sx={{ p: "10px" }}
            aria-label="attachment"
            onClick={openFilePicker}
            onTouchEnd={handleAttachTouchEnd}
          >
            <AttachFile />
          </IconButton>
          </>
          : <IconButton type="button" sx={{ p: '10px' }} aria-label="attachment" component="label">
            <input
              ref={fileInputRef}
              type="file"
              onChange={uploadFile}
              style={{
                position: "fixed",
                left: "-10000px",
                top: "0px",
                width: "1px",
                height: "1px",
                opacity: 0,
                pointerEvents: "none",
              }}
            />
            <AttachFile />
          </IconButton> 
          }
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <IconButton
            color="primary"
            sx={{ p: '10px' }}
            aria-label="directions"
            disabled={!canSubmit}
            onClick={updateFinalChatData}
            onTouchEnd={handleUpdateFinalChatDataTouchEnd}
          >
            <SendRounded />
          </IconButton>
        </Paper>
      </Box>
    </Drawer>
  );
};

export default ChatSection;
